package org.example.project.enginepersonal

import org.example.project.service.ZustandManager
import org.example.project.utils.All
import kotlin.js.Date

internal data class KontingentDayReference(
    val type: String,
    val status: String,
    val label: String,
    val colorHex: String,
    val rows: List<Pair<String, String>>
)

internal fun kontingentAbsenceType(absence: All): String =
    absence.abwesenheitsart.ifBlank {
        absence.bezeichnung.ifBlank {
            absence.art.ifBlank { "Abwesenheit" }
        }
    }

internal fun kontingentAbsenceDays(absence: All): List<String> =
    when {
        absence.tage.isNotEmpty() -> absence.tage
        absence.urlaubstag == false -> emptyList()
        else -> listOf(absence.datum).filter { it.isNotBlank() }
    }

internal fun kontingentAbsenceDisplayDays(absence: All): List<String> =
    if (kontingentAbsenceType(absence).equals("Freizeitausgleich", ignoreCase = true)) {
        kontingentAbsenceDays(absence)
    } else {
        absence.kalendertage.ifEmpty { kontingentAbsenceDays(absence) }
    }

internal fun kontingentAbsenceStatusPriority(absence: All): Int {
    if (ZustandManager.Abwesenheit.isKrankheit(kontingentAbsenceType(absence))) return 30
    return when (ZustandManager.Urlaub.fromCode(absence.zustand)) {
        ZustandManager.Urlaub.STORNIERT -> 50
        ZustandManager.Urlaub.VERWEIGERT -> 40
        ZustandManager.Urlaub.GENEHMIGT -> 30
        ZustandManager.Urlaub.IN_BEARBEITUNG -> 20
        ZustandManager.Urlaub.BEANTRAGT -> 10
        else -> 0
    }
}

internal fun kontingentAbsenceIsEffective(absence: All): Boolean =
    ZustandManager.Abwesenheit.isKrankheit(kontingentAbsenceType(absence)) ||
        absence.zustand == ZustandManager.Urlaub.GENEHMIGT

internal fun kontingentAbsenceFor(
    userId: String,
    iso: String,
    absences: List<All>
): All? = absences
    .filter {
        it.userId.ifBlank { it.kontakt } == userId &&
            it.urlaubstag != false &&
            iso in kontingentAbsenceDays(it)
    }
    .maxByOrNull(::kontingentAbsenceStatusPriority)

internal fun kontingentAbsenceReference(absence: All, iso: String): KontingentDayReference {
    val type = kontingentAbsenceType(absence)
    val isIllness = ZustandManager.Abwesenheit.isKrankheit(type)
    val isTaken = !isIllness &&
        absence.zustand == ZustandManager.Urlaub.GENEHMIGT &&
        iso <= kontingentTodayIso()
    val status = if (isTaken) {
        "genommen"
    } else {
        ZustandManager.Abwesenheit.statusText(type, absence.zustand)
    }
    val label = listOf(type, status).filter { it.isNotBlank() }.joinToString(" ")
    val color = when (absence.zustand) {
        ZustandManager.Urlaub.STORNIERT,
        ZustandManager.Urlaub.VERWEIGERT,
        ZustandManager.Urlaub.BEANTRAGT,
        ZustandManager.Urlaub.IN_BEARBEITUNG ->
            ZustandManager.Urlaub.calendarColorHexFor(absence.zustand)
        else -> when (type.trim().lowercase()) {
            "krank", "krankheit", "krankmeldung", "krankenschein" ->
                ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.KRANK)
            "freizeitausgleich", "zeitausgleich" ->
                ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FREIZEITAUSGLEICH)
            "unbezahlt frei", "fehltag" ->
                ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG)
            "sonderurlaub" ->
                ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.URLAUB)
            "urlaub" ->
                ZustandManager.Urlaub.calendarColorHexFor(
                    absence.zustand,
                    genommen = isTaken
                )
            else -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG)
        }
    }
    val days = kontingentAbsenceDays(absence)
    val from = absence.datumVon.ifBlank { days.firstOrNull().orEmpty() }
    val until = absence.datumBis.ifBlank { days.lastOrNull().orEmpty() }
    val rows = buildList {
        add("Zustand" to status)
        add("Art" to type)
        if (from.isNotBlank() || until.isNotBlank()) {
            add("Zeitraum" to "${kontingentFormatIso(from)} - ${kontingentFormatIso(until)}")
        }
        add("Tage" to days.size.toString())
    }
    return KontingentDayReference(type, status, label, color, rows)
}

internal fun kontingentDayRows(
    userId: String,
    iso: String,
    timeDay: All?,
    absences: List<All>,
    profileConfig: ZeitprofilConfig
): List<Pair<String, String>> =
    zeitTimelineRows(timeDay) +
        zeitDailyOvertimeRows(userId, iso, timeDay, profileConfig) +
        zeitTimeOffRows(userId, iso, absences, profileConfig)

private fun kontingentFormatIso(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return iso.ifBlank { "-" }
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun kontingentTodayIso(): String {
    val date = Date()
    return "${date.getFullYear().toInt()}-" +
        "${(date.getMonth().toInt() + 1).toString().padStart(2, '0')}-" +
        date.getDate().toInt().toString().padStart(2, '0')
}
